import { LoadingIndicator } from "@/Icons/loadingIndicator";
import { useTerminalsState } from "@/States/Terminals";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import {
  type ColumnType,
  percentageView,
  textView,
  weightView,
} from "@/Utils/tableColumns/functions";
import { trpc } from "@/Utils/trpc";
import { EmptyView } from "@/components/EmptyView/EmptyView";
import { Table } from "@/components/Table";
import { Card, CardComponentFilterLocation } from "@/components/pageCards/card";
import { ActiveOptions } from "@/components/pageCards/filterSort/FilterSortContext";
import { createTimeFilters } from "@/components/pageCards/filterSort/filterCreators";
import { getTimeFrame } from "@/components/pageCards/filterSort/filterTimeFrame";
import { FilterSort } from "@/components/pageCards/filterSort/types";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useHomeLoadingState } from "@/pages/home/loadingState";
import { usePdfPreview } from "@/pages/home/pdfPreview";
import { useTrans } from "@/translations";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { sum } from "lodash";
import {
  DashboardTileType,
  FilterTimeFrameValue,
} from "server/config-api/index.js";
import type { Customer } from "server/dwhApi/customers/types.js";

const SMALL_TABLE_RIGHT_ALIGN_CLASS = "justify-end pr-3";

const MAX_NR_UNEXPANDED = 3;

const CATEGORY_NONE = "NONE";

type Props = {
  timeFilter?: FilterTimeFrameValue;
  setTimeFilters?: (
    value: FilterTimeFrameValue,
    type: DashboardTileType,
  ) => void;
};

export const OverviewKeyNumbersCategory: React.FC<Props> = ({
  timeFilter: persistedTimeFilter,
  setTimeFilters,
}) => {
  const { t } = useTrans();
  const { pdfPreviewOpen } = usePdfPreview();
  const { setKeyNumbersLoading } = useHomeLoadingState();
  const { terminalConfig } = useConfigService();
  const [{ period: [selectedPeriod] = [] }, setActiveOptions] = useState<
    ActiveOptions<"period">
  >({} as any);
  const { tenantCategories } = useCommonEntitiesStore();

  const [isExpanded, setIsExpanded] = useState<boolean>(pdfPreviewOpen);

  const defaultPeriod = useMemo(
    () =>
      persistedTimeFilter ||
      terminalConfig?.defaultTimeFrame ||
      FilterTimeFrameValue.Last30Days,
    [terminalConfig, persistedTimeFilter],
  );

  const filters: FilterSort[] = useMemo(
    () => [createTimeFilters({ defaultValue: defaultPeriod })],
    [defaultPeriod],
  );

  const { currentTerminal } = useTerminalsState();

  const { startTimeISO, endTimeISO } = useMemo(() => {
    return getTimeFrame(
      selectedPeriod?.value as FilterTimeFrameValue,
      defaultPeriod,
    );
  }, [selectedPeriod, defaultPeriod]);

  const { data, isLoading, isError } = trpc.dwh.customers.get.useQuery(
    {
      startTimeISO,
      endTimeISO,
      terminalId: currentTerminal?.id || "",
    },
    {
      queryKeyHashFn: () =>
        ["customers", currentTerminal?.id, startTimeISO, endTimeISO].toString(),
      enabled: !!currentTerminal,
    },
  );

  useEffect(() => {
    setKeyNumbersLoading(isLoading);
  }, [setKeyNumbersLoading, isLoading]);

  const onFilterOptionChange = useCallback(
    (activeOptions: ActiveOptions<any>) => {
      setActiveOptions(activeOptions);

      if (setTimeFilters && activeOptions.period) {
        setTimeFilters(
          activeOptions.period[0].value as FilterTimeFrameValue,
          DashboardTileType.KeyNumbersPerCategory,
        );
      }
    },
    [setActiveOptions, setTimeFilters],
  );

  const columns: ColumnType[] = useMemo(
    () => [
      {
        label: t("circularityLabels:keyNumbersCategory"),
        name: "category",
        getView: textView,
        sortable: true,
      },
      {
        label: t("circularityLabels:keyNumbersNrTenants"),
        name: "nrTenants",
        getView: (value: any) => textView(value, SMALL_TABLE_RIGHT_ALIGN_CLASS),
        sortable: true,
      },
      {
        label: t("circularityLabels:keyNumbersGeneralWaste"),
        name: "generalWaste",
        getView: (value: any) =>
          weightView(value, SMALL_TABLE_RIGHT_ALIGN_CLASS),
        sortable: true,
      },
      {
        label: t("circularityLabels:keyNumbersRecycled"),
        name: "recycled",
        getView: (value: any) =>
          weightView(value, SMALL_TABLE_RIGHT_ALIGN_CLASS),
        sortable: true,
      },
      {
        label: t("circularityLabels:keyNumbersRecyclingRate"),
        name: "recyclingRate",
        getView: (value: any) =>
          percentageView(value, " %", SMALL_TABLE_RIGHT_ALIGN_CLASS),
        sortable: true,
      },
    ],
    [t],
  );

  const categoriesData = useMemo(() => {
    if (!data?.customers) {
      return [];
    }

    const groupedCustomersByCategory = data.customers.reduce(
      (acc, customer) => {
        const category = customer.commercialCustomerCategory || CATEGORY_NONE;

        if (!acc[category]) {
          acc[category] = [];
        }

        acc[category].push(customer);

        return acc;
      },
      {} as { [k: string]: Customer[] },
    );

    const categoryData = Object.keys(groupedCustomersByCategory)
      .map((category) => {
        const groupedCustomers = groupedCustomersByCategory[category];
        const sanityCategory = tenantCategories?.find((c) => c.id === category);

        const totalGW = sum(
          groupedCustomers.map((c) => c.totalResidualWasteWeight.quantity),
        );
        const totalWaste = sum(
          groupedCustomers.map((c) => c.totalWasteWeight.quantity),
        );
        const totalRecycled = totalWaste - totalGW;
        const recyclingRate = (totalRecycled * 100) / totalWaste;

        return {
          id: category,
          category:
            category === CATEGORY_NONE
              ? t("genericLabels:unassigned")
              : sanityCategory?.name || "",
          nrTenants: groupedCustomers.length,
          generalWaste: Number(totalGW.toFixed(1)),
          recycled: Number(totalRecycled.toFixed(1)),
          recyclingRate: recyclingRate,
        };
      })
      .sort((a, b) => a.category?.localeCompare(b.category))
      .filter((c) => c.id !== CATEGORY_NONE || c.generalWaste || c.recycled);

    return categoryData;
  }, [data, tenantCategories, t]);

  const displayedCategoriesData = useMemo(() => {
    if (!isExpanded) {
      return categoriesData.slice(0, MAX_NR_UNEXPANDED);
    }

    return categoriesData;
  }, [categoriesData, isExpanded]);

  const enableExpand = useMemo(
    () => categoriesData.length > MAX_NR_UNEXPANDED,
    [categoriesData],
  );

  const chart = useMemo(() => {
    if (isLoading) {
      return (
        <div className="flex h-full min-h-full items-center justify-center">
          <LoadingIndicator />
        </div>
      );
    }

    if (isError) {
      return <EmptyView type="failToFetch" className="min-h-[300px]" />;
    }

    if (!displayedCategoriesData.length) {
      return <EmptyView type="noDataForInterval" className="min-h-[300px]" />;
    }

    return (
      <div className="relative h-full px-4 py-1">
        <Table
          columns={columns}
          data={displayedCategoriesData}
          rightAlign
          emptyViewType="noData"
          showHeadersWhenEmpty={false}
          className="grow"
          emptyViewClassName="mt-16"
        />
        {!pdfPreviewOpen && enableExpand && (
          <div
            className="absolute bottom-1 left-1/2 flex transform-[translate(-50%,0%)] cursor-pointer text-center text-sm font-bold underline"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {t(
              isExpanded
                ? "circularityLabels:showLess"
                : "circularityLabels:showMore",
            )}
          </div>
        )}
      </div>
    );
  }, [
    isError,
    isLoading,
    displayedCategoriesData,
    columns,
    t,
    isExpanded,
    enableExpand,
    pdfPreviewOpen,
  ]);

  const fullCardView = useMemo(
    () => isError || isLoading || (isExpanded && enableExpand),
    [isError, isLoading, isExpanded, enableExpand],
  );

  return (
    <Card
      title="circularityLabels:keyNumbersPerCategory"
      filters={filters}
      onFilterOptionChange={onFilterOptionChange}
      filterLocation={CardComponentFilterLocation.RIGHT}
      halfCard={!fullCardView}
      hasYContentPadding={false}
      hasXContentPadding={false}
    >
      {chart}
    </Card>
  );
};
